import { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import './HeroNew.css';

gsap.registerPlugin(ScrollTrigger);

export default function HeroNew() {
  const { t } = useTranslation();
  const heroContainerRef = useRef<HTMLDivElement>(null);
  const sectionStickRef = useRef<HTMLElement>(null);
  const sectionOverlayRef = useRef<HTMLDivElement>(null);
  const opacityRevealRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);

  const revealText = t('home.hero.revealText');
  const revealWords = revealText.split(' ');
  const skyStars = useMemo(
    () =>
      Array.from({ length: 72 }, (_, index) => {
        const seed = (index + 1) * 97;
        return {
          id: index,
          left: `${(seed * 17) % 100}%`,
          top: `${(seed * 29) % 100}%`,
          size: 1 + (seed % 3),
          delay: `${(seed % 140) / 10}s`,
          duration: `${7 + (seed % 11)}s`,
          opacity: 0.25 + (seed % 45) / 100,
        };
      }),
    []
  );

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation timeline
      gsap
        .timeline({
          scrollTrigger: {
            trigger: heroContainerRef.current,
            start: 'top top',
            end: '+=150%',
            pin: true,
            scrub: 1,
          },
        })
        .to('.hero__cover-img', {
          scale: 2,
          z: 350,
          transformOrigin: 'center center',
          ease: 'power1.inOut',
        })
        .to(
          '.hero__cover',
          {
            '--overlay-opacity': 0,
            ease: 'power1.inOut',
          },
          '<'
        )
        .to(
          '.hero__bg',
          {
            scale: 1.1,
            filter: 'blur(0px) brightness(1)',
            ease: 'power1.inOut',
          },
          '<'
        )
        .to(
          '.hero__title',
          {
            scale: 1,
            
            opacity: 1,
            filter: 'blur(0px)',
            ease: 'power1.inOut',
          },
          '<'
        );

      // Text reveal animation (word-by-word on scroll)
      const text = opacityRevealRef.current;
      const section = sectionStickRef.current;
      const overlay = sectionOverlayRef.current;
      const cta = ctaRef.current;

      if (text && section) {
        const wordElements = text.querySelectorAll('.word');
        gsap.set(wordElements, { opacity: 0.14, filter: 'blur(4px)', y: 6 });
        if (cta) {
          gsap.set(cta, { opacity: 0, y: 16 });
        }

        const revealTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            pin: true,
            start: 'top top',
            end: '+=1800',
            scrub: 1,
          },
        });

        revealTimeline
          .to(
            overlay,
            {
              backgroundColor: '#0D0D0D',
              duration: wordElements.length + 1,
              ease: 'none',
            },
            0
          )
          .to(wordElements, {
            opacity: 1,
            filter: 'blur(0px)',
            y: 0,
            duration: 1,
            ease: 'none',
            stagger: 1,
          })
          .to(
            cta,
            {
              opacity: 1,
              y: 0,
              duration: 0.5,
              ease: 'power1.out',
            },
            '-=0.1'
          );
      }
    });

    return () => ctx.revert();
  }, [revealWords.length]);

  return (
    <main>
      <div className="hero-container" ref={heroContainerRef}>
        <section className="hero">
          <div className="hero__content">
            <video
              className="hero__bg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster="/images/Clouds.webp"
              aria-hidden="true"
              disablePictureInPicture
              disableRemotePlayback
              onLoadedMetadata={(e) => {
                e.currentTarget.defaultPlaybackRate = 0.9;
                e.currentTarget.playbackRate = 0.9;
              }}
              onPlay={(e) => {
                e.currentTarget.playbackRate = 0.9;
              }}
            >
              <source src="/images/clouds-flying-hero.mp4" type="video/mp4" />
            </video>
            <h1 className="hero__title font-bold text-white text-center" style={{ fontFamily: 'Sora, sans-serif' }}>
              Bc. Kamil<br /> Frajtko
            </h1>
          </div>
          <div className="hero__cover">
            <img
              className="hero__cover-img"
              src="/images/hero-plane.webp"
              alt="Hero background"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </section>
      </div>
      <section
        className="section-stick relative min-h-screen bg-brand-ocean flex justify-center items-center overflow-hidden text-white"
        ref={sectionStickRef}
      >
        <div ref={sectionOverlayRef} className="section-stick__start-overlay" />
        <div className="section-stick__nebula" />
        <div className="section-stick__stars" aria-hidden>
          {skyStars.map((star) => (
            <span
              key={star.id}
              className="section-stick__star"
              style={{
                left: star.left,
                top: star.top,
                width: `${star.size}px`,
                height: `${star.size}px`,
                opacity: star.opacity,
                animationDelay: star.delay,
                animationDuration: star.duration,
              }}
            />
          ))}
          <span className="section-stick__shooting section-stick__shooting--one" />
          <span className="section-stick__shooting section-stick__shooting--two" />
          <span className="section-stick__shooting section-stick__shooting--three" />
        </div>

        <div className="relative z-10 flex flex-col items-center gap-8">
          <p
            className="hero-reveal-copy opacity-reveal"
            ref={opacityRevealRef}
          >
            {revealWords.map((word, index) => (
              <span key={`${word}-${index}`} className="word hero-reveal-copy__word">
                {word}
              </span>
            ))}
          </p>
          <a
            href="/#sluzby"
            ref={ctaRef}
            className="group inline-flex items-center gap-2 rounded-xl border border-brand-green/60 bg-brand-green/20 px-7 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition-all hover:bg-brand-green/35"
          >
            {t('home.hero.cta')}
            <i className="ri-arrow-down-line transition-transform group-hover:translate-y-0.5" />
          </a>
        </div>
      </section>
    </main>
  );
}
